import { 
  FileText, 
  Users, 
  Building2, 
  Calendar, 
  User,
  HelpCircle,
  Gift,
  BookOpen,
  BarChart3,
  MessageCircle,
  CreditCard,
  Lock,
  Crown
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { FeatureAccess } from '../../types/subscription';
import MorafaaLogo from '../ui/MorafaaLogo';

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { strings, language } = useLocalization();
  const { getFeatureAccess, getCurrentPlan, state } = useSubscription();

  const featureAccess = getFeatureAccess();
  const currentPlan = getCurrentPlan();

  const navItems = [
    // 1. Agenda
    { 
      icon: <Calendar size={20} />, 
      label: strings.navigation.agenda, 
      path: '/dashboard/events', 
      roles: ['lawyer', 'office_member'],
      requiresSubscription: false
    },
    // 2. My Offices
    { 
      icon: <Building2 size={20} />, 
      label: strings.navigation.myOffices, 
      path: '/dashboard/my-offices', 
      roles: ['lawyer', 'office_member'],
      requiresSubscription: false
    },
    // 3. My Clients
    { 
      icon: <Users size={20} />, 
      label: strings.navigation.myClients, 
      path: '/dashboard/my-clients', 
      roles: ['lawyer', 'office_member'],
      requiresSubscription: false
    },
    // 4. Legal Files
    { 
      icon: <FileText size={20} />, 
      label: strings.navigation.legalFiles, 
      path: '/dashboard/legal-files', 
      roles: ['lawyer', 'office_member'],
      requiresSubscription: false
    },
    // 5. Team Members
    { 
      icon: <Users size={20} />, 
      label: strings.navigation.teamMembers, 
      path: '/dashboard/team-members', 
      roles: ['lawyer', 'office_member'],
      requiresSubscription: true,
      requiredFeature: 'teamManagement' as keyof FeatureAccess,
      upgradeMessage: language === 'ar' ? 'إدارة الفريق - متاحة في الخطة المهنية' : 'Team Management - Available in Professional Plan'
    },
    // 6. Statistics
    { 
      icon: <BarChart3 size={20} />, 
      label: strings.navigation.statistics, 
      path: '/dashboard/statistics', 
      roles: ['lawyer', 'office_member'],
      requiresSubscription: true,
      requiredFeature: 'advancedStats' as keyof FeatureAccess,
      upgradeMessage: language === 'ar' ? 'إحصائيات متقدمة - متاحة في الخطة المهنية' : 'Advanced Statistics - Available in Professional Plan'
    },
    // 7. Community
    { 
      icon: <MessageCircle size={20} />, 
      label: strings.navigation.community, 
      path: '/dashboard/community', 
      roles: ['lawyer', 'office_member'],
      requiresSubscription: true,
      requiredFeature: 'community' as keyof FeatureAccess,
      upgradeMessage: language === 'ar' ? 'المجتمع - متاح في الخطة المهنية' : 'Community - Available in Professional Plan'
    },
    // 8. Lawyer Guide
    { 
      icon: <BookOpen size={20} />, 
      label: strings.navigation.lawyerGuide, 
      path: '/dashboard/lawyer-guide', 
      roles: ['lawyer', 'office_member'],
      requiresSubscription: false
    },
    // 9. Subscriptions
    { 
      icon: <CreditCard size={20} />, 
      label: strings.navigation.subscriptions, 
      path: '/dashboard/subscriptions', 
      roles: ['lawyer', 'office_member'],
      requiresSubscription: false
    },
    // 10. Referral Program (OW-511 — 2.3.0 unhide)
    { 
      icon: <Gift size={20} />, 
      label: strings.navigation.referralProgram, 
      path: '/dashboard/referral', 
      roles: ['lawyer', 'office_member'],
      requiresSubscription: false
    },
    // 11. My Profile
    { 
      icon: <User size={20} />, 
      label: strings.navigation.myProfile, 
      path: '/dashboard/profile', 
      roles: ['lawyer', 'office_member'],
      requiresSubscription: false
    },
    // 12. Support Center
    { 
      icon: <HelpCircle size={20} />, 
      label: strings.navigation.support, 
      path: '/dashboard/support', 
      roles: ['lawyer', 'office_member'],
      requiresSubscription: false
    },
  ];

  const isActive = (path: string) => location.pathname === path;

  // Check if user has access to a feature
  const hasFeatureAccess = (feature: keyof typeof featureAccess): boolean => {
    return featureAccess[feature];
  };

  // Handle navigation - all items are accessible
  const handleNavigation = (item: any) => {
    navigate(item.path);
  };

  // Filter navigation items based on user role and subscription
  const filteredNavItems = navItems.filter(item => {
    // For now, show all items since we removed user context
    // This should be handled by the parent component or context
    return true;
  });

  return (
    <aside className="w-80 min-h-screen m3-surface m3-elevation-3 bg-gradient-to-b from-primary to-primary-dark dark:from-primary-dark dark:to-primary text-white flex flex-col sticky top-0">
      {/* Logo Section */}
      <div className="p-8">
        <div className="flex flex-col items-center space-y-4">
          <MorafaaLogo size="lg" variant="light" />
          <div className="text-center">
            <h1 className="m3-headline-medium text-white dark:text-slate-100 font-tajawal" style={{ fontFamily: 'Tajawal, system-ui, Avenir, Helvetica, Arial, sans-serif' }}>مرافعة</h1>
            <p className="m3-body-medium text-white/80 dark:text-slate-300 mt-2 font-tajawal" style={{ fontFamily: 'Tajawal, system-ui, Avenir, Helvetica, Arial, sans-serif' }}>Lawyer Portal</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-6 py-8 space-y-3 overflow-y-auto">
        {filteredNavItems.map((item, idx) => {
          const isPremium = item.requiresSubscription && item.requiredFeature;
          const hasAccess = isPremium ? hasFeatureAccess(item.requiredFeature) : true;
          
          return (
            <button
              key={idx}
              onClick={() => handleNavigation(item)}
              className={`flex items-center w-full px-6 py-4 text-left transition-all duration-300 rounded-3xl m3-ripple font-tajawal ${
                isActive(item.path)
                  ? 'bg-white/20 dark:bg-slate-700/50 text-white dark:text-slate-100 m3-elevation-2 shadow-lg'
                  : 'hover:bg-white/10 dark:hover:bg-slate-700/30 hover:text-white/90 dark:hover:text-slate-200 transition-colors'
              }`}
              title={isPremium && !hasAccess ? item.upgradeMessage : undefined}
            >
              <span className="mr-4 relative">
                {item.icon}
                {isPremium && (
                  <Crown size={12} className="absolute -top-1 -right-1 text-accent" />
                )}
              </span>
              <span className="m3-label-large font-tajawal flex-1" style={{ fontFamily: 'Tajawal, system-ui, Avenir, Helvetica, Arial, sans-serif' }}>
                {item.label}
              </span>
              {isActive(item.path) && (
                <div className="ml-auto w-2 h-2 bg-white dark:bg-slate-200 rounded-full"></div>
              )}
            </button>
          );
        })}
      </nav>

      {/* Subscription Status */}
      {currentPlan && (
        <div className="px-6 py-4 border-t border-white/10">
          <div className="bg-white/10 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Crown size={16} className="text-accent" />
              <span className="text-sm font-medium text-white">
                {language === 'ar' ? currentPlan.nameAr : currentPlan.name}
              </span>
            </div>
            <p className="text-xs text-white/70">
              {language === 'ar' 
                ? `الخطة الحالية - ${currentPlan.isFreeTier ? 'مجانية' : `${currentPlan.price} ${currentPlan.currency}`}`
                : `Current Plan - ${currentPlan.isFreeTier ? 'Free' : `${currentPlan.price} ${currentPlan.currency}`}`
              }
            </p>
            
            {/* Trial Status */}
            {state.currentSubscription && state.currentSubscription.status === 'trial' && (
              <div className="mt-3 p-2 bg-blue-500/20 border border-blue-400/30 rounded-lg">
                <div className="flex items-center space-x-2">
                  <span className="text-blue-300 text-sm">🎉</span>
                  <div>
                    <p className="text-xs font-medium text-blue-200">
                      {language === 'ar' ? 'فترة تجريبية' : 'Free Trial'}
                    </p>
                    <p className="text-xs text-blue-300">
                      {language === 'ar' 
                        ? `${state.currentSubscription.endDate ? Math.ceil((parseInt(state.currentSubscription.endDate) - Date.now()) / (1000 * 60 * 60 * 24)) : 14} أيام متبقية`
                        : `${state.currentSubscription.endDate ? Math.ceil((parseInt(state.currentSubscription.endDate) - Date.now()) / (1000 * 60 * 60 * 24)) : 14} days left`
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {currentPlan.isFreeTier && (
              <button
                onClick={() => navigate('/dashboard/subscriptions')}
                className="mt-2 w-full bg-accent hover:bg-accent-light text-primary text-xs font-medium py-2 px-3 rounded-lg transition-colors"
              >
                {language === 'ar' ? 'ترقية الخطة' : 'Upgrade Plan'}
              </button>
            )}
          </div>
        </div>
      )}

    </aside>
  );
} 